import {IMessageCode, JsonValue} from "@mindpath/shared";

export interface AppErrorOptions {
  readonly action?: {
    readonly label: string;
    readonly onClick: AppErrorAction;
  } | null;
  readonly devDetails?: {
    readonly message: string;
    readonly args?: Record<string, string | number | boolean>;
    readonly context?: JsonValue;
  };
  /**
   * What to show when the error carries no copy of its own. Needed where the client raises a code the
   * server owns without ever calling the API -- a guard, a local ticker, an interceptor that refuses
   * before the request goes out -- so there is no response body to take the copy from and no client
   * entry for that code. A message the API did send still wins over this.
   */
  readonly copy?: {
    readonly title: string;
    /** Plain text: `\n` breaks and `{support:key}` placeholders, no markup. */
    readonly message: string;
  };
}

export class AppErrorImpl extends Error implements AppError {
  readonly messageCode: IMessageCode;
  readonly traceId?: string;
  readonly title: string;
  readonly options?: AppErrorOptions;

  constructor(messageCode: IMessageCode, title: string, message: string, options?: AppErrorOptions) {
    super(message);
    this.name = messageCode.code ?? this.name;
    this.messageCode = messageCode;
    // shown under the dialog so support can find the request in the API logs
    this.traceId = messageCode.traceId || undefined;
    this.title = title;
    this.message = message;
    this.options = options;
  }
}

export interface AppError extends Error {
  readonly messageCode: IMessageCode;
  readonly traceId?: string;
  readonly title: string;
  /** Plain text with `\n` breaks and `{support:key}` placeholders. Render it with `app-message-text`. */
  readonly message: string;
  readonly options?: AppErrorOptions;
}

export type AppErrorAction = typeof AppErrorAction[keyof typeof AppErrorAction];
export const AppErrorAction = Object.freeze({
  ClearError: 'ClearError',
  HistoryBack: 'HistoryBack',
  BackToFinder: 'BackToFinder',
  BackToOtp: 'BackToOtp',
  BackToContactInfo: 'BackToContactInfo',
  RequestNewOtp: 'RequestNewOtp',
});
